import {
  DeleteOutlined,
  // DownOutlined,
  InboxOutlined, PlusOutlined,
  QuestionCircleOutlined,
  // RightOutlined,
  SaveOutlined,
  WarningFilled
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Button,
  Card,
  Checkbox,
  Col,
  ColorPicker,
  DatePicker,
  Divider,
  message,
  Radio,
  Row,
  // Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  Upload,
  UploadFile,
} from 'antd';
import { RcFile, UploadChangeParam } from 'antd/lib/upload';
import dayjs from 'dayjs';
import { FineUploaderBasic } from 'fine-uploader/lib/core';
import React, { useEffect } from 'react';
import {
  Controller, FormProvider, useFieldArray, useForm,
  useWatch
} from 'react-hook-form';
import { TFunction, useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { DropdownElement } from 'common/components/DropdownType';
import HeaderPage from 'common/components/HeaderPage';
import Input from 'common/components/Input';
import SelectFile from 'common/components/SelectFile';
import { refreshTokenService } from 'common/services/authenticate';
import {
  getAccessToken,
  setAccessToken,
  setRefreshToken,
} from 'common/services/common/storage';
import {
  createClientService,
  getClientByIdService,
  updateClientService,
} from 'common/services/extends/client';
import {
  CreateClientDataTypes,
  FileUploadTypes,
} from 'common/services/extends/client/types';
import { ROUTE_PATHS, URL_CONST_CLIENT } from 'common/utils/constant';
import { clientSchema } from 'common/utils/schemas';

interface DueDate {
  reqPaymentDate: Date | null;
  endDate: Date | null;
  amount: number;
}

type ClientFormTypes = {
  name: string;
  taxCode: string;
  dateRange: string[] | null;
  logo: string;
  brandColor: string;
  contractNumber: string;
  corporateAccountName: string;
  corporateAccountMail: string;
  corporateAccountPhone: string;
  technicalSupportName: string;
  technicalSupportMail: string;
  technicalSupportPhone: string;
  contractFiles: FileUploadTypes[];
  package?: OptionType;
  transactionMethod?: number;
  transactions?: DueDate[];
  totalAmount?: number;
  activePlan?: string;
  isUseDuoKnowledgeBase: boolean;
  knowledgeUseCase?: OptionType;
};

const DEFAULT_VALUES: ClientFormTypes = {
  name: '',
  taxCode: '',
  dateRange: null,
  logo: '',
  brandColor: '',
  contractNumber: '',
  corporateAccountName: '',
  corporateAccountMail: '',
  corporateAccountPhone: '',
  technicalSupportName: '',
  technicalSupportMail: '',
  technicalSupportPhone: '',
  contractFiles: [],
  transactionMethod: 1,
  transactions: [
    {
      reqPaymentDate: null,
      endDate: null,
      amount: 0,
    },
  ],
  totalAmount: 0,
  isUseDuoKnowledgeBase: false,
};

const mappingFieldName = (field: string, t: TFunction<'translation', undefined>): string => {
  switch (field) {
    case 'name':
      return t('client.name');
    case 'taxCode':
      return t('client.taxCode');
    case 'contractNumber':
      return t('client.contractNumber');
    case 'dateRange':
      return t('client.dateRange');
    case 'corporateAccountName':
      return t('client.corporateAccountName');
    case 'corporateAccountMail':
      return t('client.corporateAccountMail');
    case 'corporateAccountPhone':
      return t('client.corporateAccountPhone');
    case 'technicalSupportName':
      return t('client.technicalSupportName');
    case 'technicalSupportMail':
      return t('client.technicalSupportMail');
    case 'package.value':
      return t('client.package');
    default:
      return field;
  }
};

const mappingCodeFieldMessage = (code: string, field?: string) => {
  switch (code) {
    case 'unique':
      return `${field} đã tồn tại`;
    case 'after':
      return 'Ngày bắt đầu phải lớn hơn ngày hôm nay';
    case 'contractNumber':
      return 'client.contractNumber';
    case 'corporateAccountName':
      return 'client.corporateAccountName';
    case 'corporateAccountMail':
      return 'client.corporateAccountMail';
    case 'corporateAccountPhone':
      return 'client.corporateAccountPhone';
    default:
      return 'Có lỗi';
  }
};

const ClientEditDetail: React.FC<ActiveRoles> = ({ roleIndex, roleUpdate }) => {
  const { RangePicker } = DatePicker;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = getAccessToken();
  const [fileList, setFileList] = React.useState([]);
  const [isChangePackage, setIsChangePackage] = React.useState(false);
  const [isKnowledgeDirty, setIsKnowledgeDirty] = React.useState(false);
  const queryClient = useQueryClient();

  const idParams = Number(searchParams.get('id'));
  const method = useForm<ClientFormTypes>({
    defaultValues: DEFAULT_VALUES,
    resolver: yupResolver(clientSchema),
    mode: 'onChange',
  });

  const { fields, append, remove } = useFieldArray({
    control: method.control,
    name: 'transactions',
  });

  const paymentMethods = [
    {
      value: 1, label: `${t('client.oneTime')}`
    },
    { value: 2, label: `${t('client.multiple')}` },
    { value: 3, label: `${t('client.skip')}` },
  ];

  const installmentsWatch = useWatch({ control: method.control, name: 'transactions' });
  const dateRangeWatch = useWatch({ control: method.control, name: 'dateRange' });
  const servicePackageWatch = useWatch({ control: method.control, name: 'package' });
  const isUseDuoKnowledgeBaseWatch = useWatch({ control: method.control, name: 'isUseDuoKnowledgeBase' });

  const queryClientDataById = ['getClientDataById', idParams];

  const { data: clientData, isLoading: clientDataLoading } = useQuery(
    queryClientDataById,
    () => {
      if (idParams) {
        return getClientByIdService(idParams);
      }
      return undefined;
    },
    {
      enabled: roleIndex && !!idParams,
      onSuccess: (data) => {
        if (data) {
          const { startDate } = data.clientData;
          const { endDate } = data.clientData;
          method.reset({
            name: data.clientData.name || '',
            taxCode: data.clientData.taxCode || '',
            dateRange: startDate && endDate ? [startDate, endDate] : null,
            logo: data.clientData.logo || '',
            brandColor: data.clientData.brandColor || '',
            contractNumber: data.clientData.contractNumber || '',
            corporateAccountName: data.clientData.corporateAccountName || '',
            corporateAccountMail: data.clientData.corporateAccountMail || '',
            corporateAccountPhone: data.clientData.corporateAccountPhone || '',
            technicalSupportName: data.clientData.technicalSupportName || '',
            technicalSupportMail: data.clientData.technicalSupportMail || '',
            technicalSupportPhone: data.clientData.technicalSupportPhone || '',
            contractFiles: data.clientData.contractFiles || [],
            package: data.servicePackageData
              ? {
                label: data.servicePackageData.name,
                value: data.servicePackageData.id,
              }
              : undefined,
            transactionMethod: data.transMethod,
            transactions: (data.transactions
              ? data.transactions.map((item) => ({
                reqPaymentDate: dayjs((item.reqPaymentDate || (dayjs(new Date()).add(1, 'day')))).toDate(),
                endDate: dayjs((item.endDate || null)).toDate(),
                amount: item.amount
              }))
              : [{
                reqPaymentDate: dayjs(new Date()).add(1, 'day').toDate(),
                endDate: dayjs(endDate).toDate(),
                amount: 0
              }]
            ),
            activePlan: data.subscriptionEndDate,
            isUseDuoKnowledgeBase: data.isUseDuoKnowledgeBase
              || !!(data.useCases && data.useCases.length > 0)
              || false,
            knowledgeUseCase: (() => {
              if (data.knowledgeUseCaseData) {
                return {
                  label: data.knowledgeUseCaseData.name,
                  value: data.knowledgeUseCaseData.id,
                };
              }
              if (data.useCases && data.useCases.length > 0) {
                return {
                  label: data.useCases[0].name,
                  value: data.useCases[0].id,
                };
              }
              return undefined;
            })(),
          });
          if (!idParams) {
            append({
              reqPaymentDate: dayjs().toDate(),
              endDate: dayjs(endDate).toDate(),
              amount: 0
            });
          }
          setFileList(data.clientData.contractFiles as any);
          setIsKnowledgeDirty(false);
        } else {
          method.reset(DEFAULT_VALUES);
        }
      },
    }
  );

  console.log('===clientData==', clientData);

  React.useEffect(() => {
    const installments = method.getValues('transactions') || [];
    if (!installments.length) return;

    installments.forEach((item, index) => {
      const isLast = index === installments.length - 1;
      let computedEndDate: Date | null = null;

      if (installments.length === 1) {
        computedEndDate = dateRangeWatch?.[1]
          ? dayjs(dateRangeWatch[1]).toDate()
          : null;
      } else if (!isLast) {
        const nextStart = installments[index + 1]?.reqPaymentDate;
        computedEndDate = nextStart
          ? dayjs(nextStart).subtract(1, 'days').toDate()
          : null;
      } else {
        computedEndDate = dateRangeWatch?.[1]
          ? dayjs(dateRangeWatch[1]).toDate()
          : null;
      }

      const currentEndDate = installments[index]?.endDate;

      const hasChanged = (!currentEndDate && computedEndDate)
        || (currentEndDate && computedEndDate && !dayjs(currentEndDate).isSame(computedEndDate));

      if (hasChanged) {
        method.setValue(`transactions.${index}.endDate`, computedEndDate, {
          shouldDirty: false,
          shouldTouch: false,
        });
      }
    });
  }, [
    dateRangeWatch,
    JSON.stringify(method.watch('transactions')?.map((i) => i?.reqPaymentDate)),
  ]);

  useEffect(() => {
    if (idParams) {
      setIsChangePackage(clientData?.servicePackageData?.id !== servicePackageWatch?.value);
    }
  }, [clientData?.servicePackageData?.id, idParams, method, servicePackageWatch?.value]);

  useEffect(() => {
    method.setValue('totalAmount', Number(installmentsWatch?.map((item) => Number(item.amount)).reduce((total, item) => total + item, 0)));
  }, [installmentsWatch, method]);

  const { mutate: createMutate, isLoading: createLoading } = useMutation(
    ['createClientMutate'],
    async (params: CreateClientDataTypes) => createClientService(params),
    {
      onSuccess: () => {
        message.success('Client created successfully');
        navigate(ROUTE_PATHS.CLIENT_MANAGEMENT);
      },
      onError: (errors: ErrorResponse[]) => {
        if (errors.length > 0) {
          errors.forEach((error: ErrorResponse) => {
            method.setError((error.field.split('.'))[1] as keyof ClientFormTypes, {
              message: mappingCodeFieldMessage(error.code, mappingFieldName(error.field.split('.')[1], t)) || error.message || '',
            });
          });
        } else {
          message.error(t('message.createError'));
        }
      },
    }
  );
  const { mutate: updateMutate, isLoading: updateLoading } = useMutation(
    ['client-update'],
    async (params: CreateClientDataTypes) => updateClientService(idParams, params),
    {
      onSuccess: () => {
        message.success('Client edited successfully');
        queryClient.invalidateQueries(queryClientDataById);
      },
      onError: (errors: ErrorResponse[]) => {
        if (errors.length > 0) {
          message.error(t('message.updateError'));
          if (errors[0].code === 'active') {
            message.error(errors[0].message);
          } else {
            errors.forEach((error: ErrorResponse) => {
              method.setError(error.field as keyof ClientFormTypes, {
                message: mappingCodeFieldMessage(error.code, mappingFieldName(error.field.split('.')[1], t)) || error.message || '',
              });
            });
          }
        } else {
          message.error(t('message.updateError'));
        }
      },
    }
  );

  const onSubmit = async () => {
    const isValid = await method.trigger();
    if (!isValid) {
      return;
    }

    const formData = method.getValues();
    const rawTransactions = formData.transactionMethod === 2
      ? (formData.transactions || [])
      : [{
        reqPaymentDate: formData.transactions?.[0].reqPaymentDate
          || dayjs(formData.dateRange?.[0]).add(1, 'day').endOf('day').toISOString(),
        endDate: formData?.dateRange?.[1],
        amount: formData.totalAmount || 0,
      }];

    const dataRequest: CreateClientDataTypes = {
      client: {
        name: formData.name || '',
        taxCode: formData.taxCode || '',
        startDate: formData.dateRange
          ? dayjs(formData.dateRange[0]).toISOString()
          : null,
        endDate: formData.dateRange
          ? dayjs(formData.dateRange[1]).toISOString()
          : null,
        logo: formData.logo || '',
        brandColor: formData.brandColor || '',
        contractNumber: formData.contractNumber || '',
        corporateAccountName: formData.corporateAccountName || '',
        corporateAccountMail: formData.corporateAccountMail || '',
        corporateAccountPhone: formData.corporateAccountPhone || '',
        technicalSupportName: formData.technicalSupportName || '',
        technicalSupportMail: formData.technicalSupportMail || '',
        technicalSupportPhone: formData.technicalSupportPhone || '',
        contractFiles: formData.contractFiles || [],
        useCases: formData.isUseDuoKnowledgeBase && formData.knowledgeUseCase
          ? [Number((formData.knowledgeUseCase as OptionType).value)]
          : [],
      },
      servicePackageId: formData.package
        ? (formData.package as OptionType).value as number
        : undefined,
      transMethod: formData.transactionMethod || 1,

      transactions: rawTransactions.map((item) => {
        const reqDate = dayjs(item.reqPaymentDate).toISOString();
        const end = dayjs(item.endDate).endOf('day').toISOString();
        return {
          reqPaymentDate: reqDate,
          endDate: end,
          amount: item.amount,
        };
      }),
      isUseDuoKnowledgeBase: formData.isUseDuoKnowledgeBase,
      knowledgeUseCaseId: formData.knowledgeUseCase
        ? (formData.knowledgeUseCase as OptionType).value as number
        : undefined,

    };

    if (idParams) {
      updateMutate(dataRequest);
    } else {
      createMutate(dataRequest);
    }
  };

  const uploader = new FineUploaderBasic({
    autoUpload: false,
    request: {
      endpoint: URL_CONST_CLIENT.FILE_CLIENT_UPLOAD_CHUNK,
      customHeaders: {
        Authorization: `Bearer ${token}`,
      },
    },
    chunking: {
      enabled: true,
      mandatory: true,
      partSize: 1000000, // 1MB
      success: {
        jsonPayload: true,
        endpoint: URL_CONST_CLIENT.FILE_CLIENT_MERGE_CHUNK,
        params(fileId: number) {
          const uuid = uploader.getUuid(fileId);
          const fileName = (uploader.getFile(fileId) as File).name;
          return {
            chunkedUuid: uuid,
            qqfilename: fileName,
          };
        },
      },
    },
    callbacks: {
      onComplete(
        id: number,
        name: string,
        res: {
          success: boolean;
          data: {
            path: string;
            id: string;
            name: string;
          };
        }
      ) {
        if (res.success) {
          const contractFiles = method.getValues('contractFiles');
          method.setValue('contractFiles', [...contractFiles, res.data]);
        }
      },
      onError: async (
        id: number,
        name: string,
        errorReason: string,
        xhr: XMLHttpRequest
      ) => {
        if (xhr.status === 401) {
          await refreshTokenService().then((data) => {
            setAccessToken(data.accessToken);
            setRefreshToken(data.refreshToken);
            uploader.setCustomHeaders({
              Authorization: `Bearer ${data.accessToken}`,
            });
            uploader.retry(id);
          });
        }
      },
    },
  });

  const handleChange = (fileTypes: UploadChangeParam<UploadFile>) => {
    setFileList(fileTypes.fileList as any);
  };

  const handleUpload = () => {
    if (fileList.length === 0) {
      message.warning('No files selected!');
      return;
    }
    uploader.addFiles(fileList.map((file) => (file as any).originFileObj));
    uploader.uploadStoredFiles();
    message.success(t('message.uploadSuccess'));
  };

  const beforeUpload = (file: RcFile) => {
    const isPDF = file.type === 'application/pdf';
    if (!isPDF) {
      message.error('You can only upload PDF file!');
    }
    return isPDF || Upload.LIST_IGNORE;
  };

  return (
    <FormProvider<ClientFormTypes> {...method}>
      <HeaderPage
        fixed
        title={idParams ? 'Edit' : 'Create'}
        rightHeader={(
          <Button
            type="primary"
            loading={clientDataLoading || updateLoading}
            onClick={method.handleSubmit(onSubmit)}
            disabled={
              !roleUpdate
              || (!method.formState.isDirty && !isKnowledgeDirty)
              || !method.formState.isValid
            }
          >
            <SaveOutlined />
            {t('system.save')}
          </Button>
        )}
      />
      <Spin spinning={clientDataLoading || createLoading || updateLoading}>
        <div className="t-mainlayout_wrapper">
          <Row gutter={16}>
            <Col xxl={24}>
              <Card>
                <Row gutter={16}>
                  <Col span={12} className="u-mb-12">
                    <Typography.Text strong>Logo </Typography.Text>
                    <Controller
                      name="logo"
                      render={({ field: { value, onChange } }) => (
                        <div className="u-mt-8">
                          <SelectFile
                            value={value}
                            name="logo"
                            handleSelect={onChange}
                            handleDelete={() => onChange(undefined)}
                            title=""
                          />
                        </div>
                      )}
                    />
                  </Col>
                  <Col span={6} className="u-mb-12">
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'start',
                        flexDirection: 'column',
                      }}
                    >
                      <Typography.Text strong>{t('client.brandColor')}</Typography.Text>
                      <Controller
                        name="brandColor"
                        render={({ field: { value, onChange } }) => (
                          <ColorPicker
                            disabledAlpha
                            allowClear
                            className="u-mt-8"
                            value={value}
                            onChange={(color) => onChange(color.toHexString())}
                            onClear={() => onChange(undefined)}
                          />
                        )}
                      />
                    </div>
                  </Col>
                  <Col span={12} className="u-mb-12">
                    <Typography.Text strong>{t('client.companyName')}</Typography.Text>
                    <Typography.Text strong type="danger">
                      *
                    </Typography.Text>
                    <Controller
                      name="name"
                      render={({
                        field: { value, onChange },
                        fieldState: { error },
                      }) => (
                        <Input
                          className="u-mt-8"
                          name="name"
                          placeholder={`${t('system.input')} ${t(
                            'system.name'
                          )}`}
                          value={value}
                          onChange={(e) => {
                            const input = e.target.value;
                            if (input.length <= 51) {
                              onChange(input);
                            }
                          }}
                          error={error?.message}
                          size="large"
                        />
                      )}
                    />
                  </Col>
                  <Col span={12} className="u-mb-12">
                    <Typography.Text strong>
                      {t('client.taxCode')}
                      {' '}
                    </Typography.Text>
                    <Typography.Text strong type="danger">
                      *
                    </Typography.Text>
                    <Controller
                      name="taxCode"
                      render={({
                        field: { value, onChange },
                        fieldState: { error },
                      }) => (
                        <Input
                          className="u-mt-8"
                          name="taxCode"
                          placeholder={`${t('system.input')} ${t(
                            'client.taxCode'
                          )}`}
                          value={value}
                          onChange={onChange}
                          error={error?.message}
                          size="large"
                        />
                      )}
                    />
                  </Col>
                  <Divider />
                  <Typography.Title level={3}>{t('client.contactDetail')}</Typography.Title>
                  <Card
                    className="u-mt-12"
                    style={{ width: '100%' }}
                    title={(
                      <Typography.Title level={4}>
                        {t('client.corporationAccount')}
                      </Typography.Title>
                    )}
                  >
                    <Row gutter={16}>
                      <Col span={12} className="u-mb-12">
                        <Typography.Text strong>
                          {t('system.name')}
                          {' '}
                        </Typography.Text>
                        <Typography.Text strong type="danger">
                          *
                        </Typography.Text>
                        <Controller
                          name="corporateAccountName"
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <Input
                              className="u-mt-8"
                              name="corporateAccountName"
                              placeholder={`${t('system.input')} ${t(
                                'system.name'
                              )}`}
                              value={value}
                              onChange={onChange}
                              error={error?.message}
                              size="large"
                            />
                          )}
                        />
                      </Col>
                      <Col span={12} className="u-mb-12">
                        <Typography.Text strong>
                          {t('system.email')}
                          {' '}
                        </Typography.Text>
                        <Typography.Text strong type="danger">
                          *
                        </Typography.Text>
                        <Controller
                          name="corporateAccountMail"
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <Input
                              className="u-mt-8"
                              name="corporateAccountMail"
                              placeholder={`${t('system.input')} ${t(
                                'system.email'
                              )}`}
                              value={value}
                              onChange={onChange}
                              error={error?.message}
                              size="large"
                            />
                          )}
                        />
                      </Col>
                      <Col span={12} className="u-mb-12">
                        <Typography.Text strong>
                          {t('system.phone')}
                          {' '}
                        </Typography.Text>
                        <Typography.Text strong type="danger">
                          *
                        </Typography.Text>
                        <Controller
                          name="corporateAccountPhone"
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <Input
                              className="u-mt-8"
                              name="corporateAccountPhone"
                              placeholder={`${t('system.input')} ${t(
                                'system.phone'
                              )}`}
                              value={value}
                              onChange={onChange}
                              error={error?.message}
                              size="large"
                              type="number"
                            />
                          )}
                        />
                      </Col>
                    </Row>
                  </Card>
                  <Card
                    className="u-mt-12"
                    style={{ width: '100%' }}
                    title={(
                      <Typography.Title level={4}>
                        {t('client.technicalSupport')}
                      </Typography.Title>
                    )}
                  >
                    <Row gutter={16}>
                      <Col span={12} className="u-mb-12">
                        <Typography.Text strong>
                          {t('system.name')}
                          {' '}
                        </Typography.Text>
                        <Controller
                          name="technicalSupportName"
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <Input
                              className="u-mt-8"
                              name="technicalSupportName"
                              placeholder={`${t('system.input')} ${t(
                                'system.name'
                              )}`}
                              value={value}
                              onChange={onChange}
                              error={error?.message}
                              size="large"
                            />
                          )}
                        />
                      </Col>
                      <Col span={12} className="u-mb-12">
                        <Typography.Text strong>
                          {t('system.email')}
                          {' '}
                        </Typography.Text>
                        <Controller
                          name="technicalSupportMail"
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <Input
                              className="u-mt-8"
                              name="technicalSupportMail"
                              placeholder={`${t('system.input')} ${t(
                                'system.email'
                              )}`}
                              value={value}
                              onChange={onChange}
                              error={error?.message}
                              size="large"
                            />
                          )}
                        />
                      </Col>
                      <Col span={12} className="u-mb-12">
                        <Typography.Text strong>
                          {t('system.phone')}
                          {' '}
                        </Typography.Text>
                        <Controller
                          name="technicalSupportPhone"
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <Input
                              className="u-mt-8"
                              name="technicalSupportPhone"
                              placeholder={`${t('system.input')} ${t(
                                'system.phone'
                              )}`}
                              value={value}
                              onChange={onChange}
                              error={error?.message}
                              size="large"
                              type="number"
                            />
                          )}
                        />
                      </Col>
                    </Row>
                  </Card>
                  <Divider />
                  <Col span={12} className="u-mb-12">
                    <Typography.Text strong>
                      {t('client.contractNumber')}
                      {' '}
                    </Typography.Text>
                    <Controller
                      name="contractNumber"
                      render={({
                        field: { value, onChange },
                        fieldState: { error },
                      }) => (
                        <Input
                          className="u-mt-8"
                          name="contractNumber"
                          placeholder={`${t('system.input')} ${t(
                            'client.contractNumber'
                          )}`}
                          value={value}
                          onChange={onChange}
                          error={error?.message}
                          size="large"
                        />
                      )}
                    />
                  </Col>
                  <Col span={12} className="u-mb-12">
                    <Typography.Text strong>
                      {t('client.startDateNEndDate')}
                      {' '}
                    </Typography.Text>
                    <Typography.Text strong type="danger">
                      *
                    </Typography.Text>
                    <Controller
                      name="dateRange"
                      render={({
                        field: { value, onChange },
                        fieldState: { error },
                      }) => (
                        <>
                          <RangePicker
                            className="u-mt-8"
                            allowClear
                            size="large"
                            onChange={(val) => onChange(val)}
                            value={
                              value
                                ? [dayjs(value[0]), dayjs(value[1])]
                                : undefined
                            }
                            style={{ width: '100%' }}
                            disabledDate={(current) => current && current < dayjs().add(1, 'day').startOf('day')}
                          />
                          {error && (
                            <span className="a-input_errorMessage">
                              {error.message}
                            </span>
                          )}
                        </>
                      )}
                    />
                  </Col>
                  <Col span={12} className="u-mt-12">
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <div>
                        <Typography.Text strong>
                          {t('client.contractFiles')}
                          {' '}
                        </Typography.Text>
                      </div>
                      <Upload.Dragger
                        multiple
                        onChange={handleChange}
                        beforeUpload={beforeUpload}
                        accept="application/pdf"
                        showUploadList={false}
                        className="u-mt-8"
                        style={{
                          width: '100%',
                        }}
                      >
                        <p className="ant-upload-drag-icon">
                          <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">
                          Click or drag file to this area to upload (only accept
                          PDF)
                        </p>
                      </Upload.Dragger>
                      {fileList?.length > 0 && (
                        <div className="u-mt-8">
                          <Typography.Title level={5}>
                            Selected files
                          </Typography.Title>
                          {fileList.map((file: any, idx) => (
                            <div
                              className="u-mt-8"
                              key={`${file.uid}-${file.name}-${idx.toString()}`}
                              style={{
                                color: '#1d8daa',
                                cursor: 'pointer',
                                width: 'fit-content',
                              }}
                            >
                              {file.name}
                            </div>
                          ))}
                        </div>
                      )}
                      <div
                        style={{
                          width: '100%',
                          display: 'flex',
                          justifyContent: 'center',
                        }}
                      >
                        <Button
                          type="primary"
                          onClick={handleUpload}
                          className="u-mt-8"
                        >
                          Upload Selected Files
                        </Button>
                        <Button
                          type="dashed"
                          onClick={() => {
                            setFileList([]);
                            method.setValue('contractFiles', []);
                          }}
                          className="u-mt-8"
                          style={{
                            marginLeft: '16px',
                          }}
                        >
                          Clear
                        </Button>
                      </div>
                    </div>
                  </Col>
                  <Divider />
                  <Typography.Title level={3}>
                    {t('client.limitConfiguration')}
                  </Typography.Title>
                  <Card className="u-mt-12" style={{ width: '100%' }}>
                    <Row gutter={16}>
                      <Col span={12} className="u-mb-12">
                        <Typography.Text strong>
                          {t('sidebar.package')}
                          {' '}
                        </Typography.Text>
                        <Typography.Text strong type="danger">
                          *
                        </Typography.Text>
                        <Controller
                          name="package"
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <>
                              <DropdownElement
                                type="servicePackages"
                                placeholder={`${t('system.select')} ${t(
                                  'sidebar.package'
                                )}`}
                                locale="vi"
                                value={value}
                                onChange={onChange}
                                isGetOption
                                size="large"
                              />
                              {error && (
                                <span className="a-input_errorMessage">
                                  {(error as any).value.message}
                                </span>
                              )}
                            </>
                          )}
                        />
                      </Col>
                    </Row>
                  </Card>
                  <Divider />
                  {(!idParams || isChangePackage) && (
                    <>
                      <Typography.Title level={3}>
                        {t('client.transactionMethod')}
                      </Typography.Title>
                      <Col>
                        <Controller
                          name="transactionMethod"
                          render={({
                            field: { value, onChange },
                            fieldState: { error },
                          }) => (
                            <>
                              <Radio.Group
                                className="u-mt-8"
                                onChange={onChange}
                                value={value}
                              >
                                {paymentMethods.map((item) => (
                                  <Row justify="space-between" key={item.value}>
                                    <Radio value={item.value}>
                                      <Typography.Text>{item.label}</Typography.Text>
                                    </Radio>
                                    <Tooltip title={item.label}>
                                      <QuestionCircleOutlined />
                                    </Tooltip>
                                  </Row>
                                ))}
                              </Radio.Group>
                              {error && (
                                <span className="a-input_errorMessage">
                                  {error.message}
                                </span>
                              )}
                            </>
                          )}
                        />
                        {!!idParams && (
                          <div>
                            {method.watch('activePlan') && (
                              <Tag style={{ padding: '4px 8px' }} className="u-mt-8">
                                <WarningFilled />
                                <Typography.Text>
                                  {`${t('client.anouncement')} ${dayjs(method.watch('activePlan')).format('YYYY-MM-DD')} .${t('client.anouncementConfirm')}`}
                                </Typography.Text>
                              </Tag>
                            )}
                            {method.watch('transactionMethod') === 1 && (
                              <Col className="u-mt-8" span={6}>
                                <Typography.Text>{t('client.applyDate')}</Typography.Text>
                                <Controller
                                  control={method.control}
                                  name={`transactions.${0}.reqPaymentDate`}
                                  rules={{ required: 'Start date is required' }}
                                  render={({
                                    field:
                                    { value, onChange }, fieldState: { error }
                                  }) => (
                                    <div>
                                      <DatePicker
                                        value={value ? dayjs(value) : dayjs(new Date())}
                                        onChange={(date) => onChange(date?.startOf('day').toISOString())}
                                        style={{ width: '100%' }}
                                        size="large"
                                        placeholder={t('client.applyDate')}
                                        disabled={!dateRangeWatch?.[0] || false}
                                        disabledDate={(current) => {
                                          const tomorrow = dayjs().add(1, 'day').startOf('day');

                                          // Block today and past dates
                                          if (current && current < tomorrow) {
                                            return true;
                                          }

                                          // Then apply range restriction
                                          if (dateRangeWatch?.[0] && dateRangeWatch?.[1]) {
                                            const min = dayjs(dateRangeWatch[0]).add(1, 'day').startOf('day');
                                            const max = dayjs(dateRangeWatch[1]).subtract(1, 'day').startOf('day');
                                            return current < min || current > max;
                                          }

                                          return false;
                                        }}
                                      />
                                      {error && <div className="a-input_errorMessage">{error.message}</div>}
                                    </div>
                                  )}
                                />
                              </Col>
                            )}
                          </div>
                        )}
                      </Col>
                      {method.watch('transactionMethod') === 2 && (
                        <Col>
                          {fields.map((field, index) => (
                            <Row gutter={4} align="middle" key={field.id} className="u-mt-12">
                              <Col span={1}>
                                <Typography.Text strong>
                                  {`${index + 1}.`}
                                </Typography.Text>
                              </Col>

                              {/* Amount */}
                              <Col span={8}>
                                <Typography.Text>{t('client.amount')}</Typography.Text>
                                <Controller
                                  control={method.control}
                                  name={`transactions.${index}.amount`}
                                  rules={{ required: 'Amount is required' }}
                                  render={({
                                    field: { value, onChange }, fieldState: { error }
                                  }) => (
                                    <div>
                                      <Input
                                        value={value}
                                        onChange={onChange}
                                        placeholder="Amount (VND)"
                                        size="large"
                                      />
                                      {error && <div className="a-input_errorMessage">{error.message}</div>}
                                    </div>
                                  )}
                                />
                              </Col>

                              {/* Start Date */}
                              <Col span={6}>
                                <Typography.Text>{t('system.startDate')}</Typography.Text>
                                <Controller
                                  control={method.control}
                                  name={`transactions.${index}.reqPaymentDate`}
                                  rules={{
                                    required: 'Start date is required',
                                    validate: {
                                      // FIRST PRIORITY: Must be tomorrow or later
                                      notTodayOrPast: (value) => {
                                        if (!value) return true;
                                        const selected = dayjs(value).startOf('day');
                                        const tomorrow = dayjs().add(1, 'day').startOf('day');
                                        return selected.isSame(tomorrow)
                                          || selected.isAfter(tomorrow)
                                          ? true
                                          : 'Start date must be tomorrow or later';
                                      },

                                      // SECOND PRIORITY: Must be within allowed range (if exists)
                                      withinRange: (value) => {
                                        if (!value || !dateRangeWatch?.[0]
                                          || !dateRangeWatch?.[1]) return true;

                                        const date = dayjs(value);
                                        const min = dayjs(dateRangeWatch[0]).add(1, 'day').startOf('day');
                                        const max = dayjs(dateRangeWatch[1]).subtract(1, 'day').startOf('day');

                                        return (date.isSame(min) || date.isAfter(min))
                                          && (date.isSame(max) || date.isBefore(max))
                                          ? true
                                          : 'Start date must be within the allowed range';
                                      },
                                    },
                                  }}
                                  render={({
                                    field: { value, onChange },
                                    fieldState: { error }
                                  }) => (
                                    <div>
                                      <DatePicker
                                        value={value ? dayjs(value) : null}
                                        onChange={(date) => {
                                          onChange(date ? date.startOf('day').toISOString() : null);
                                        }}
                                        style={{ width: '100%' }}
                                        size="large"
                                        placeholder={t('system.startDate')}
                                        disabled={!dateRangeWatch?.[0]}
                                        disabledDate={(current) => {
                                          const tomorrow = dayjs().add(1, 'day').startOf('day');

                                          // Block today and past dates
                                          if (current && current < tomorrow) {
                                            return true;
                                          }

                                          // Then apply range restriction
                                          if (dateRangeWatch?.[0] && dateRangeWatch?.[1]) {
                                            const min = dayjs(dateRangeWatch[0]).add(1, 'day').startOf('day');
                                            const max = dayjs(dateRangeWatch[1]).subtract(1, 'day').startOf('day');
                                            return current < min || current > max;
                                          }

                                          return false;
                                        }}
                                      />
                                      {error && <div className="a-input_errorMessage">{error.message}</div>}
                                    </div>
                                  )}
                                />
                              </Col>

                              {/* End Date - Auto-filled & Read-only */}
                              <Col span={6}>
                                <Typography.Text>{t('system.endDate')}</Typography.Text>
                                <Controller
                                  control={method.control}
                                  name={`transactions.${index}.endDate`}
                                  rules={{
                                    required: 'End date is required',
                                    validate: (value) => !!value || 'End date is missing',
                                  }}
                                  render={({ field: { value }, fieldState: { error } }) => (
                                    <div>
                                      <DatePicker
                                        value={value ? dayjs(value) : null}
                                        disabled
                                        style={{ width: '100%' }}
                                        size="large"
                                        placeholder={t('system.endDate')}
                                      />
                                      {error && <div className="a-input_errorMessage">{error.message}</div>}
                                    </div>
                                  )}
                                />
                              </Col>
                              {/* Delete button */}
                              <Col span={2}>
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => remove(index)}
                                  disabled={fields.length === 1}
                                />
                              </Col>
                            </Row>
                          ))}

                          {/* Add new installment */}
                          <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            className="u-mt-16"
                            onClick={() => append({ reqPaymentDate: null, amount: 0 })}
                            disabled={fields.length > 11}
                          >
                            {t('news.addRow')}
                          </Button>
                        </Col>
                      )}
                      <Col>
                        <Row justify="end">
                          <div className="u-mt-16">
                            <Typography.Text strong>
                              {t('client.total')}
                            </Typography.Text>
                            <Controller
                              name="totalAmount"
                              defaultValue=""
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Input
                                  type="number"
                                  className="u-mt-8"
                                  value={value}
                                  onChange={onChange}
                                  error={error?.message}
                                  size="large"
                                />
                              )}
                            />
                          </div>
                        </Row>
                      </Col>
                    </>
                  )}
                </Row>

                <Typography.Title level={3}>
                  {t('client.knowledgeConfig')}
                </Typography.Title>
                <Controller
                  name="isUseDuoKnowledgeBase"
                  render={({ field: { value, onChange } }) => (
                    <Checkbox
                      checked={value}
                      onChange={(e) => {
                        onChange(e.target.checked);
                        setIsKnowledgeDirty(true);
                        if (!e.target.checked) {
                          method.setValue('knowledgeUseCase', undefined, { shouldDirty: true });
                        }
                      }}
                    >
                      {t('client.useDuoKnowledgeBase')}
                    </Checkbox>
                  )}
                />
                {isUseDuoKnowledgeBaseWatch && (
                  <div style={{ marginTop: 10 }}>
                    <Typography.Text strong>
                      {t('client.knowledgeUseCase')}
                    </Typography.Text>
                    <Controller
                      name="knowledgeUseCase"
                      render={({
                        field: { value: kbValue, onChange: kbOnChange },
                        fieldState: { error }
                      }) => (
                        <DropdownElement
                          isGetOption
                          type="knowledgeUseCase"
                          value={kbValue}
                          onChange={(val) => {
                            kbOnChange(val);
                            method.setValue('knowledgeUseCase', val, { shouldDirty: true });
                            setIsKnowledgeDirty(true);
                          }}
                          locale="vi"
                          isShowSearch
                          error={error?.message}
                        />
                      )}
                    />
                  </div>
                )}
              </Card>
            </Col>
          </Row>
        </div>
      </Spin>
    </FormProvider>
  );
};

export default ClientEditDetail;
